import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { updateAppointmentStatusThunk, updatePaymentStatusThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import CancelAppointmentModal from "./CancelAppointmentModal";

const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4 text-gray-400" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = ({ className = "" }) => (
  <svg className={`w-3.5 h-3.5 shrink-0 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "CHECKED_IN", label: "Checked-In" },
  { value: "IN_CONSULTATION", label: "In Consultation" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "NO_SHOW", label: "No-Show" },
];

const VISIT_TYPE_OPTIONS = [
  { value: "ALL", label: "All Visit Types" },
  { value: "NEW", label: "New Visit" },
  { value: "FOLLOW_UP", label: "Follow-Up" },
  { value: "EMERGENCY", label: "Emergency" },
];

const PAYMENT_OPTIONS = [
  { value: "ALL", label: "All Payment Statuses" },
  { value: "PAID", label: "Paid Only" },
  { value: "UNPAID", label: "Unpaid Only" },
];

const RowStatusDropdown = ({ currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = [
    { value: "SCHEDULED", label: "Scheduled" },
    { value: "CHECKED_IN", label: "Checked-In" },
    { value: "IN_CONSULTATION", label: "In Consultation" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "NO_SHOW", label: "No-Show" },
  ];

  const activeOption = options.find((o) => o.value === currentStatus) || options[0];

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-2.5 py-1 text-xs font-bold text-gray-800 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl shadow-2xs transition-all cursor-pointer flex items-center gap-1.5"
      >
        <span>{activeOption.label}</span>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? "rotate-180 text-primary-600" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1 divide-y divide-gray-100 text-left">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onStatusChange(opt.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2 text-xs font-semibold cursor-pointer transition-all flex items-center justify-between hover:bg-gray-50 ${
                currentStatus === opt.value ? "bg-primary-50 text-primary-700 font-bold" : "text-gray-700"
              }`}
            >
              <span>{opt.label}</span>
              {currentStatus === opt.value && <CheckIcon className="text-primary-600" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AppointmentsTableView = ({ appointments, selectedDate, onOpenReceiptMaker }) => {
  const dispatch = useDispatch();
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterVisitType, setFilterVisitType] = useState("ALL");
  const [filterPayment, setFilterPayment] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  // Custom Dropdown Open State ('status' | 'visit' | 'payment' | null)
  const [openDropdown, setOpenDropdown] = useState(null);

  const containerRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizeDate = (d) => {
    if (!d) return "";
    const str = String(d).trim();
    if (str.includes("T")) return str.split("T")[0];
    if (str.includes(" ")) return str.split(" ")[0];
    return str;
  };

  const targetDate = normalizeDate(selectedDate);

  const filteredAppointments = appointments.filter((app) => {
    // If strict date comparison yields 0 items, keep appointments fetched by server query
    if (app.appointmentDate && normalizeDate(app.appointmentDate) !== targetDate && appointments.length > 20) {
      return false;
    }
    if (filterStatus !== "ALL" && app.status !== filterStatus) return false;
    if (filterVisitType !== "ALL" && app.visitType !== filterVisitType) return false;
    if (filterPayment !== "ALL" && app.paymentStatus !== filterPayment) return false;

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase().trim();
      const matchName = app.patientName?.toLowerCase().includes(q);
      const matchPhone = app.patientPhone?.includes(q);
      const matchPid = app.PID?.toLowerCase().includes(q);
      const matchToken = String(app.tokenNumber) === q;
      return matchName || matchPhone || matchPid || matchToken;
    }
    return true;
  });

  const handleStatusChange = (AID, newStatus) => {
    if (newStatus === "CANCELLED" || newStatus === "NO_SHOW") {
      const targetApp = appointments.find((a) => a.AID === AID);
      if (targetApp) {
        setCancelTarget(targetApp);
        return;
      }
    }
    dispatch(
      updateAppointmentStatusThunk({
        AID,
        status: newStatus,
        appointmentDate: selectedDate,
      })
    );
  };

  const handleTogglePayment = (app) => {
    const nextStatus = app.paymentStatus === "PAID" ? "UNPAID" : "PAID";
    dispatch(
      updatePaymentStatusThunk({
        AID: app.AID,
        paymentStatus: nextStatus,
        paymentMethod: app.paymentMethod || "CASH",
        consultationFee: app.consultationFee,
        appointmentDate: selectedDate,
      })
    );
  };

  const getStatusBadge = (status, reason) => {
    let badge;
    switch (status) {
      case "CHECKED_IN":
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">Checked-In</span>;
        break;
      case "IN_CONSULTATION":
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 animate-pulse">In Consultation</span>;
        break;
      case "COMPLETED":
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">Completed</span>;
        break;
      case "CANCELLED":
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">Cancelled</span>;
        break;
      case "NO_SHOW":
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-800">No-Show</span>;
        break;
      default:
        badge = <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">Scheduled</span>;
        break;
    }
    return (
      <div>
        {badge}
        {reason && (
          <div className="text-[10px] text-red-600 font-semibold mt-1 max-w-[160px] truncate" title={reason}>
            Reason: {reason}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 md:p-6 space-y-6">
      {/* Search & Custom Filters Section */}
      <div className="space-y-3 pb-4 border-b border-gray-100" ref={containerRef}>
        {/* Row 1: Full-Width Search Input */}
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search by Patient Name, Phone, PID or Token #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-gray-200 text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-primary-500 outline-none shadow-2xs bg-white"
          />
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <SearchIcon />
          </div>
        </div>

        {/* Row 2: 3 Custom Dropdowns Side-by-Side */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Custom Dropdown 1: Consultation Status */}
          <div className="relative">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setOpenDropdown(openDropdown === "status" ? null : "status")}
              className={`w-full px-3.5 py-2.5 rounded-2xl border bg-white text-xs font-bold text-gray-800 flex items-center justify-between shadow-2xs cursor-pointer transition-all ${
                openDropdown === "status" ? "border-primary-500 ring-2 ring-primary-500/20" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="truncate">
                {STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label}
              </span>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${openDropdown === "status" ? "rotate-180" : ""}`} />
            </div>

            {openDropdown === "status" && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-xl z-30 py-1.5 divide-y divide-gray-100 max-h-60 overflow-y-auto">
                {STATUS_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => {
                      setFilterStatus(opt.value);
                      setOpenDropdown(null);
                    }}
                    className={`px-3.5 py-2.5 text-xs font-semibold cursor-pointer transition-all flex items-center justify-between ${
                      filterStatus === opt.value
                        ? "bg-primary-50 text-primary-700 font-bold"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {filterStatus === opt.value && <CheckIcon className="text-primary-600" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Custom Dropdown 2: Visit Type */}
          <div className="relative">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setOpenDropdown(openDropdown === "visit" ? null : "visit")}
              className={`w-full px-3.5 py-2.5 rounded-2xl border bg-white text-xs font-bold text-gray-800 flex items-center justify-between shadow-2xs cursor-pointer transition-all ${
                openDropdown === "visit" ? "border-primary-500 ring-2 ring-primary-500/20" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="truncate">
                {VISIT_TYPE_OPTIONS.find((o) => o.value === filterVisitType)?.label}
              </span>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${openDropdown === "visit" ? "rotate-180" : ""}`} />
            </div>

            {openDropdown === "visit" && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-xl z-30 py-1.5 divide-y divide-gray-100 max-h-60 overflow-y-auto">
                {VISIT_TYPE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => {
                      setFilterVisitType(opt.value);
                      setOpenDropdown(null);
                    }}
                    className={`px-3.5 py-2.5 text-xs font-semibold cursor-pointer transition-all flex items-center justify-between ${
                      filterVisitType === opt.value
                        ? "bg-primary-50 text-primary-700 font-bold"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {filterVisitType === opt.value && <CheckIcon className="text-primary-600" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Custom Dropdown 3: Payment Status */}
          <div className="relative">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setOpenDropdown(openDropdown === "payment" ? null : "payment")}
              className={`w-full px-3.5 py-2.5 rounded-2xl border bg-white text-xs font-bold text-gray-800 flex items-center justify-between shadow-2xs cursor-pointer transition-all ${
                openDropdown === "payment" ? "border-primary-500 ring-2 ring-primary-500/20" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="truncate">
                {PAYMENT_OPTIONS.find((o) => o.value === filterPayment)?.label}
              </span>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${openDropdown === "payment" ? "rotate-180" : ""}`} />
            </div>

            {openDropdown === "payment" && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-xl z-30 py-1.5 divide-y divide-gray-100 max-h-60 overflow-y-auto">
                {PAYMENT_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => {
                      setFilterPayment(opt.value);
                      setOpenDropdown(null);
                    }}
                    className={`px-3.5 py-2.5 text-xs font-semibold cursor-pointer transition-all flex items-center justify-between ${
                      filterPayment === opt.value
                        ? "bg-primary-50 text-primary-700 font-bold"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {filterPayment === opt.value && <CheckIcon className="text-primary-600" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Master Data Table */}
      <div className="border border-gray-200 rounded-2xl shadow-xs bg-white min-h-[240px]">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-100/80 border-b border-gray-200 font-bold text-gray-700 uppercase tracking-wider">
            <tr>
              <th className="p-3.5">Token</th>
              <th className="p-3.5">Patient Details</th>
              <th className="p-3.5">Time & Visit</th>
              <th className="p-3.5">Fee & Payment</th>
              <th className="p-3.5">Consultation Status</th>
              <th className="p-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-medium">
            {filteredAppointments.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-400 font-semibold">
                  No appointments found for the selected filter.
                </td>
              </tr>
            ) : (
              filteredAppointments.map((app) => (
                <tr key={app.AID} className="hover:bg-gray-50/80 transition-all">
                  <td className="p-3.5 font-black text-gray-900">
                    #{app.tokenNumber}
                  </td>
                  <td className="p-3.5">
                    <span className="font-bold text-gray-900 block">
                      {app.patientName}
                    </span>
                    <span className="text-[11px] text-gray-400 font-normal">
                      📞 {app.patientPhone} {app.PID ? `| PID: ${app.PID}` : ""}
                    </span>
                  </td>
                  <td className="p-3.5">
                    <span className="font-bold text-gray-800 block">
                      {app.slotStartTime} - {app.slotEndTime}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-100 text-gray-600 inline-block mt-0.5">
                      {app.visitType}
                    </span>
                  </td>
                  <td className="p-3.5">
                    <span className="font-black text-gray-900 block">
                      ₹{app.consultationFee}
                    </span>
                    <span
                      onClick={() => handleTogglePayment(app)}
                      className={`text-[10px] font-black px-2 py-0.5 rounded-full cursor-pointer inline-block mt-0.5 ${
                        app.paymentStatus === "PAID"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {app.paymentStatus === "PAID" ? "✓ PAID" : "UNPAID"}
                    </span>
                  </td>
                  <td className="p-3.5">{getStatusBadge(app.status, app.cancelledReason)}</td>
                  <td className="p-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onOpenReceiptMaker(app)}
                        className="px-3 py-1 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer"
                      >
                        Receipt
                      </button>

                      <RowStatusDropdown
                        currentStatus={app.status}
                        onStatusChange={(newStatus) => handleStatusChange(app.AID, newStatus)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cancel Appointment Modal */}
      {cancelTarget && (
        <CancelAppointmentModal
          appointment={cancelTarget}
          onClose={() => setCancelTarget(null)}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default AppointmentsTableView;
